import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Stepper } from '../ui/Stepper';
import { CustomTooltip } from '../ui/tooltip';
import { Badge } from '../ui/badge';
import { Sparkle, CheckCircle, Upload, Database, WarningCircle, Stack } from '@phosphor-icons/react';
import { SignatureFlipCard } from '../ui/SignatureFlipCard';
import { DeconvolutionAlgorithmSelector } from '../ui/DeconvolutionAlgorithmSelector';
import { signaturesApi, deconvolutionApi, fileApi, dataOverviewApi } from '../../services/endpoints';
import { saveNavigationState, restoreNavigationState } from '../../utils/navigationStateManager';
import type { Signature, UploadedFile, AlgorithmInfo, AlgorithmRecommendation, Dataset } from '../../services/types';

/** Extract the backend's `detail` message from a caught error, if present. */
function getErrorDetail(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
  }
  return fallback;
}

export function SelectSignature() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [selectedSignatureId, setSelectedSignatureId] = useState<string>('');
  const [selectedAlgorithms, setSelectedAlgorithms] = useState<string[]>([]);
  const [jobName, setJobName] = useState('');
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [loadingSignatures, setLoadingSignatures] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Deconvolution-related state
  const [algorithms, setAlgorithms] = useState<AlgorithmInfo[]>([]);
  const [recommendations, setRecommendations] = useState<AlgorithmRecommendation[]>([]);
  const [loadingAlgorithms, setLoadingAlgorithms] = useState(true);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [dwlsOptions, setDwlsOptions] = useState({
    cell_type_column: 'cell_type',
    dwls_method: 'mast' as 'mast' | 'mast_optimized' | 'seurat',
    dwls_submethod: 'DampenedWLS' as 'DampenedWLS' | 'OLS' | 'SVR',
    diff_cutoff: 0.5,
    pval_cutoff: 0.01,
    ncores: 1,
  });

  // Get data from location state, with fallback to sessionStorage
  const dataType = location.state?.dataType || restoreNavigationState('select-signature')?.dataType || 'private';
  const fileId = location.state?.fileId || restoreNavigationState('select-signature')?.fileId;
  const fileData: UploadedFile | undefined = location.state?.fileData || restoreNavigationState('select-signature')?.fileData;
  const datasetId = location.state?.datasetId || restoreNavigationState('select-signature')?.datasetId;
  const [publicDatasets, setPublicDatasets] = useState<Dataset[]>([]);

  useEffect(() => {
    // Save current state to sessionStorage for page reload recovery
    if (location.state) {
      saveNavigationState('select-signature', location.state);
    }

    if (dataType === 'public') {
      const stored = sessionStorage.getItem('selectedPublicDatasets');
      if (stored) {
        try{
          setPublicDatasets(JSON.parse(stored));
        } catch(e) {
          console.error('Failed to parse public datasets from sessionStorage:', e);
          setPublicDatasets([]);
        } 
      }
    }
    
    // Fetch available signatures and algorithms
    Promise.all([fetchSignatures(), fetchAlgorithms()]);
    // location.state and the fetch* functions are intentionally excluded:
    // location.state is a fresh object on every navigation and would refire
    // this on every render; fetchSignatures/fetchAlgorithms only need to run
    // once per dataType.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataType]);

  // Fetch recommendations when file ID changes
  useEffect(() => {
    if (fileId && dataType === 'private') {
      fetchRecommendations();
    }
    // fetchRecommendations intentionally excluded: it closes over
    // selectedAlgorithms only to decide whether to auto-select a top
    // recommendation, not to re-fetch — including it would re-request
    // recommendations every time the user toggles an algorithm checkbox.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, dataType]);

  const fetchSignatures = async () => {
    try {
      setLoadingSignatures(true);
      const response = await signaturesApi.list({}, 1, 100);
      setSignatures(response.data.signatures || []);
      
      // Auto-select first signature if available
      if (response.data.signatures && response.data.signatures.length > 0) {
        setSelectedSignatureId(response.data.signatures[0].id);
      }
    } catch (err: unknown) {
      console.error('Failed to fetch signatures:', err);
      setError('Failed to load available signatures. Please try again.');
    } finally {
      setLoadingSignatures(false);
    }
  };

  const fetchAlgorithms = async () => {
    try {
      setLoadingAlgorithms(true);
      const response = await deconvolutionApi.listAlgorithms();
      if (response.data?.algorithms) {
        setAlgorithms(response.data.algorithms);
      }
    } catch (err: unknown) {
      console.error('Failed to fetch algorithms:', err);
      setError('Failed to load deconvolution algorithms.');
    } finally {
      setLoadingAlgorithms(false);
    }
  };

  const fetchRecommendations = async () => {
    if (!fileId || dataType !== 'private') return;
    
    try {
      setLoadingRecommendations(true);
      const response = await deconvolutionApi.getRecommendations(parseInt(fileId, 10), 'private');
      if (response.data?.recommendations) {
        setRecommendations(response.data.recommendations);
        // Auto-select top recommended algorithm
        if (response.data.recommendations.length > 0 && selectedAlgorithms.length === 0) {
          const topAlgo = response.data.recommendations[0].algorithm.name;
          setSelectedAlgorithms([topAlgo]);
        }
      }
    } catch (err: unknown) {
      console.error('Failed to fetch recommendations:', err);
      // Don't show error - recommendations are optional
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const steps = [
    { label: 'Upload', status: 'completed' as const },
    { label: 'Validate', status: 'completed' as const },
    { label: 'Select Signature', status: 'current' as const },
    { label: 'Run', status: 'upcoming' as const }
  ];

  const handleUseRecommended = () => {
    if (recommendations.length > 0) {
      // Use top 3 recommended algorithms
      const topAlgos = recommendations.slice(0, 3).map(r => r.algorithm.name);
      setSelectedAlgorithms(topAlgos);
    } else {
      // Use signature's compatible algorithms
      const selectedSig = signatures.find(s => s.id === selectedSignatureId);
      if (selectedSig && selectedSig.compatible_algorithms) {
        const algos = typeof selectedSig.compatible_algorithms === 'string'
          ? selectedSig.compatible_algorithms.split(',').map(a => a.trim())
          : selectedSig.compatible_algorithms;
        setSelectedAlgorithms(algos.slice(0, 3));
      }
    }
  };

  const handleSubmit = async () => {
    const submitFileId = dataType === 'public' ? datasetId : fileId;
    
    if (!submitFileId || !selectedSignatureId || selectedAlgorithms.length === 0) {
      setError('Please ensure a file is uploaded, a signature is selected, and at least one algorithm is chosen.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Fetch sample count based on data source
      let nSamples: number | undefined;
      try {
        if (dataType === 'private' && fileId) {
          // For private uploads, get from file details
          const fileResponse = await fileApi.getFile(fileId);
          nSamples = fileResponse.data?.sample_count || undefined;
        } else if (dataType === 'public' && datasetId) {
          // For public datasets, get from dataset overview
          const overviewResponse = await dataOverviewApi.getOverview(datasetId, 'human');
          nSamples = overviewResponse.data?.sample_count || undefined;
        }
      } catch (err) {
        console.warn('Failed to fetch sample count:', err);
        // Continue without sample count if fetch fails
      }

      // Submit jobs for each selected algorithm using deconvolution API
      const jobPromises = selectedAlgorithms.map(algorithm =>
        deconvolutionApi.runDeconvolution({
          dataset_id: dataType === 'public' ? submitFileId : parseInt(submitFileId, 10),
          dataset_source: dataType === 'public' ? 'public' : 'private',
          signature_id: parseInt(selectedSignatureId, 10),
          algorithm: algorithm,
          tissue_context: jobName || undefined,
          user_id: parseInt(user?.id || '1', 10),
          n_samples: nSamples,
          algorithm_params: algorithm === 'dwls' ? {
            cell_type_column: dwlsOptions.cell_type_column,
            dwls_method: dwlsOptions.dwls_method,
            dwls_submethod: dwlsOptions.dwls_submethod,
            diff_cutoff: dwlsOptions.diff_cutoff,
            pval_cutoff: dwlsOptions.pval_cutoff,
            ncores: dwlsOptions.ncores,
          } : undefined,
        })
      );

      await Promise.all(jobPromises);
      
      // Navigate to job queue
      navigate('/jobs');
    } catch (err: unknown) {
      console.error('Failed to submit job:', err);
      setError(getErrorDetail(err, 'Failed to submit analysis. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Stepper steps={steps} />

        <div className="mb-8">
          <span className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase block mb-3">
            Configure analysis
          </span>
          <h1 className="text-foreground mb-2">Select signature.</h1>
          <p className="text-muted-foreground">
            Select the appropriate gene signature and deconvolution algorithms for your dataset.
          </p>
        </div>

        {/* Data Source Info */}
        <Card className="p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 ${dataType === 'public' ? 'bg-primary/10' : 'bg-accent/10'} rounded-lg flex items-center justify-center`}>
              {dataType === 'public' ? (
                <Database className="w-5 h-5 text-primary" />
              ) : (
                <Upload className="w-5 h-5 text-accent" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4>Data Source</h4>
                <Badge variant={dataType === 'public' ? 'default' : 'secondary'}>
                  {dataType === 'public' ? 'Public Dataset' : 'Private Upload'}
                </Badge>
              </div>
              {dataType === 'public' && publicDatasets.length > 0 ? (
                <div className="space-y-1">
                  {publicDatasets.map((dataset, idx) => (
                    <p key={idx} className="text-muted-foreground">
                      {dataset.study_title || dataset.project_id} ({dataset.sample_count ?? 'N/A'} samples)
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  {dataType === 'public' 
                    ? 'Public dataset from recount3' 
                    : `Your uploaded gene expression data (${fileData?.gene_count?.toLocaleString() || 'N/A'} genes × ${fileData?.sample_count || 'N/A'} samples)`}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <div className="p-4 mb-6 border border-border rounded-lg">
            <div className="flex items-start gap-3">
              <WarningCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Main Configuration */}
          <div className="lg:col-span-3 space-y-6">
            {/* Job Name */}
            <Card className="p-6">
              <label className="block mb-2">
                Analysis Name <span className="text-muted-foreground">(optional)</span>
              </label>
              <input
                type="text"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
                placeholder="e.g., Lung_Cancer_Cohort_2024"
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-hidden focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
              />
              <p className="text-muted-foreground mt-2">
                Give your analysis a memorable name for easy reference later
              </p>
            </Card>

            {/* Signature Selection - Featured Section */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Stack className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl">Select Cell-Type Signature</h3>
                    <p className="text-sm text-muted-foreground">Choose a signature that matches your tissue type</p>
                  </div>
                </div>
                <CustomTooltip content="Choose a signature matrix that matches your tissue type for optimal deconvolution results." />
              </div>
              
              {loadingSignatures ? (
                <div className="flex items-center justify-center p-12">
                  <motion.div
                    className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  />
                  <span className="ml-4 text-muted-foreground">Loading signatures...</span>
                </div>
              ) : signatures.length === 0 ? (
                <div className="p-4 border border-border rounded-lg">
                  <p className="text-muted-foreground text-sm">No signatures available. Please contact support.</p>
                </div>
              ) : (
                <motion.div 
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: { staggerChildren: 0.1 }
                    }
                  }}
                >
                  {signatures.map((sig) => (
                    <motion.div
                      key={sig.id}
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: { opacity: 1, y: 0 }
                      }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                    >
                      <SignatureFlipCard
                        signature={sig}
                        isSelected={selectedSignatureId === sig.id}
                        onSelect={setSelectedSignatureId}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </Card>

            {/* Algorithm Selection with Deconvolution Component */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3>Select Algorithms</h3>
                <Button
                  variant="ghost"
                  onClick={handleUseRecommended}
                  className="flex items-center gap-2"
                  disabled={!selectedSignatureId}
                  size="sm"
                >
                  <Sparkle className="w-4 h-4" />
                  Use Recommended
                </Button>
              </div>

              <DeconvolutionAlgorithmSelector
                selectedAlgorithms={selectedAlgorithms}
                onSelectionChange={setSelectedAlgorithms}
                availableAlgorithms={algorithms}
                recommendations={recommendations}
                loading={loadingAlgorithms || loadingRecommendations}
                disabled={!selectedSignatureId}
                maxSelections={3}
              />

              {selectedAlgorithms.includes('dwls') && (
                <div className="mt-4 p-4 border border-border rounded-lg bg-muted/30">
                  <h4 className="mb-3">DWLS Settings</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 text-sm text-muted-foreground">Cell Type Column</label>
                      <input
                        type="text"
                        value={dwlsOptions.cell_type_column}
                        onChange={(e) => setDwlsOptions(prev => ({ ...prev, cell_type_column: e.target.value }))}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-background"
                        placeholder="cell_type"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-sm text-muted-foreground">Signature Method</label>
                      <select
                        value={dwlsOptions.dwls_method}
                        onChange={(e) => setDwlsOptions(prev => ({ ...prev, dwls_method: e.target.value as 'mast' | 'mast_optimized' | 'seurat' }))}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-background"
                      >
                        <option value="mast">mast</option>
                        <option value="mast_optimized">mast_optimized</option>
                        <option value="seurat">seurat</option>
                      </select>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm text-muted-foreground">Solver</label>
                      <select
                        value={dwlsOptions.dwls_submethod}
                        onChange={(e) => setDwlsOptions(prev => ({ ...prev, dwls_submethod: e.target.value as 'DampenedWLS' | 'OLS' | 'SVR' }))}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-background"
                      >
                        <option value="DampenedWLS">DampenedWLS</option>
                        <option value="OLS">OLS</option>
                        <option value="SVR">SVR</option>
                      </select>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm text-muted-foreground">Cores</label>
                      <input
                        type="number"
                        min={1}
                        value={dwlsOptions.ncores}
                        onChange={(e) => setDwlsOptions(prev => ({ ...prev, ncores: Math.max(1, Number(e.target.value) || 1) }))}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-background"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-sm text-muted-foreground">diff_cutoff</label>
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        value={dwlsOptions.diff_cutoff}
                        onChange={(e) => setDwlsOptions(prev => ({ ...prev, diff_cutoff: Math.max(0, Number(e.target.value) || 0) }))}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-background"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-sm text-muted-foreground">pval_cutoff</label>
                      <input
                        type="number"
                        step="0.001"
                        min={0}
                        value={dwlsOptions.pval_cutoff}
                        onChange={(e) => setDwlsOptions(prev => ({ ...prev, pval_cutoff: Math.max(0, Number(e.target.value) || 0) }))}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-background"
                      />
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Summary Panel */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <h3 className="mb-4">Analysis Summary</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-muted-foreground mb-1">Dataset</p>
                  <p className="text-foreground font-medium">{fileData?.filename || 'N/A'}</p>
                  <p className="text-sm text-muted-foreground">
                    {fileData?.gene_count?.toLocaleString() || 'N/A'} genes
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {fileData?.sample_count || 'N/A'} samples
                  </p>
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-muted-foreground mb-1">Signature</p>
                  <p className="text-foreground font-medium">
                    {selectedSignatureId ? 
                      signatures.find(s => s.id === selectedSignatureId)?.name || 'Selected' 
                      : 'Not selected'}
                  </p>
                  {selectedSignatureId && (
                    <p className="text-sm text-muted-foreground">
                      {signatures.find(s => s.id === selectedSignatureId)?.cell_type_count} cell types
                    </p>
                  )}
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-muted-foreground mb-2">Algorithms ({selectedAlgorithms.length})</p>
                  <div className="space-y-1">
                    {selectedAlgorithms.length > 0 ? (
                      selectedAlgorithms.map(algo => (
                        <div key={algo} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                          <span className="text-sm text-foreground font-medium">{algo}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No algorithms selected</p>
                    )}
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-muted-foreground mb-1">Estimated Runtime</p>
                  <p className="text-foreground font-medium">
                    {selectedAlgorithms.length > 0 ? 
                      `${selectedAlgorithms.length * 3}-${selectedAlgorithms.length * 5} min` 
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Running multiple algorithms lets you compare results and validate findings across methods.
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button variant="secondary" onClick={() => navigate('/upload')}>
            Back
          </Button>
          <Button
            variant="default"
            disabled={!selectedSignatureId || selectedAlgorithms.length === 0 || submitting}
            onClick={handleSubmit}
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              'Submit Analysis'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
